import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Button,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import Iconify from '../../../components/iconify';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import ReimbursementStatusBadge from './ReimbursementStatusBadge';
import { REIMBURSEMENT_STATUS } from '../constants';
import { getReimbursementById, updateReimbursementStatus, cancelReimbursement } from '../../../services/reimbursementService';
import { isAdmin, isManager, isHR, checkComponentPermission } from '../../../utils/share';
import Loading from '../../../layouts/loading';
import { getFileDownloadUrl } from '../../../services/fileUploadService';

const ReimbursementDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.login?.user || null);
  const userId = user?.id;
  const [reimbursement, setReimbursement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachmentDownloadUrl, setAttachmentDownloadUrl] = useState(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const isAdminOrManagerOrHR = isAdmin() || isManager() || isHR();
  const isOwnRequest = reimbursement?.userId === userId;
  const canApproveReject =
    (isAdmin() || (isManager() && !isOwnRequest)) &&
    checkComponentPermission('APPROVE_REIMBURSEMENT') &&
    reimbursement?.status === REIMBURSEMENT_STATUS.PENDING;
  const canCancel =
    isOwnRequest &&
    reimbursement?.status === REIMBURSEMENT_STATUS.PENDING &&
    checkComponentPermission('DELETE_REIMBURSEMENT');

  const fetchDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getReimbursementById(id);
      const data = response?.data?.data;
      setReimbursement(data);

      if (data?.attachmentKey) {
        try {
          const urlResponse = await getFileDownloadUrl(data.attachmentKey);
          setAttachmentDownloadUrl(urlResponse);
        } catch {
          // Non-critical — PDF link unavailable
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error fetching reimbursement details');
      navigate('/reimbursements');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch {
      return dateString;
    }
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await updateReimbursementStatus(id, { status: 'approved' });
      toast.success('Reimbursement request approved successfully');
      setApproveDialogOpen(false);
      fetchDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error approving reimbursement request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setIsSubmitting(true);
    try {
      await updateReimbursementStatus(id, { status: 'rejected', rejectionReason });
      toast.success('Reimbursement request rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error rejecting reimbursement request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setIsSubmitting(true);
    try {
      await cancelReimbursement(id);
      toast.success('Reimbursement request cancelled successfully');
      setCancelDialogOpen(false);
      navigate('/reimbursements');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error cancelling reimbursement request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Reimbursements', path: '/reimbursements' },
    { label: 'Details', path: '' },
  ];

  if (isLoading) return <Loading open />;

  if (!reimbursement) return null;

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              startIcon={<Iconify icon="eva:arrow-back-fill" />}
              onClick={() => navigate('/reimbursements')}
              variant="outlined"
              size="small"
            >
              Back
            </Button>
            <Typography variant="h5">Reimbursement Details</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            {canApproveReject && (
              <>
                <Button variant="contained" color="success" onClick={() => setApproveDialogOpen(true)}>
                  Approve
                </Button>
                <Button variant="contained" color="error" onClick={() => setRejectDialogOpen(true)}>
                  Reject
                </Button>
              </>
            )}
            {canCancel && (
              <Button variant="outlined" color="error" onClick={() => setCancelDialogOpen(true)}>
                Cancel Request
              </Button>
            )}
          </Stack>
        </Stack>

        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
              <Typography variant="h6">Request #{reimbursement.id}</Typography>
              <ReimbursementStatusBadge status={reimbursement.status} />
            </Stack>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              {isAdminOrManagerOrHR && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Employee
                  </Typography>
                  <Typography variant="body1">
                    {reimbursement.employee?.userName || reimbursement.employee?.email || '-'}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date
                </Typography>
                <Typography variant="body1">{formatDate(reimbursement.date)}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {parseFloat(reimbursement.amount || 0).toFixed(2)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {reimbursement.description || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Attachment
                </Typography>
                {attachmentDownloadUrl ? (
                  <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Iconify icon="material-symbols:preview" />}
                      onClick={() => setPdfPreviewOpen(true)}
                    >
                      Preview Receipt
                    </Button>
                    <Tooltip title="Download PDF">
                      <IconButton
                        size="small"
                        color="primary"
                        component="a"
                        href={attachmentDownloadUrl}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Iconify icon="material-symbols:download" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No attachment
                  </Typography>
                )}
              </Grid>

              {reimbursement.reviewerDetails && reimbursement.reviewerDetails.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Selected Reviewers
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {reimbursement.reviewerDetails.map((reviewer) => (
                      <Chip
                        key={reviewer.id}
                        label={`${reviewer.userName} (${
                          reviewer.userRoleId === 2 ? 'Manager' : reviewer.userRoleId === 6 ? 'HR' : 'Admin'
                        })`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Grid>
              )}

              {reimbursement.status !== REIMBURSEMENT_STATUS.PENDING && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Reviewed By
                    </Typography>
                    <Typography variant="body1">
                      {reimbursement.approver?.userName || reimbursement.approver?.email || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Reviewed At
                    </Typography>
                    <Typography variant="body1">{formatDate(reimbursement.reviewedAt)}</Typography>
                  </Grid>
                  {reimbursement.rejectionReason && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Rejection Reason
                      </Typography>
                      <Typography variant="body1" color="error.main">
                        {reimbursement.rejectionReason}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Submitted On
                </Typography>
                <Typography variant="body1">{formatDate(reimbursement.createdAt)}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify icon="material-symbols:picture-as-pdf" width={24} sx={{ color: 'error.main' }} />
              <Typography variant="h6">Receipt Preview</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Download PDF">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Iconify icon="material-symbols:download" />}
                  component="a"
                  href={attachmentDownloadUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download
                </Button>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton size="small" onClick={() => window.open(attachmentDownloadUrl, '_blank')}>
                  <Iconify icon="material-symbols:open-in-new" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => setPdfPreviewOpen(false)}>
                <Iconify icon="material-symbols:close" />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, position: 'relative' }}>
          {pdfLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                bgcolor: 'background.paper',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <iframe
            key={attachmentDownloadUrl}
            src={attachmentDownloadUrl}
            title="PDF Receipt Preview"
            width="100%"
            height="100%"
            style={{ border: 'none', display: 'block', minHeight: '70vh' }}
            onLoad={() => setPdfLoading(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Approve Reimbursement Request</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to approve this reimbursement request?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <LoadingButton variant="contained" color="success" onClick={handleApprove} loading={isSubmitting}>
            Approve
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reject Reimbursement Request</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason *"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectionReason('');
            }}
          >
            Cancel
          </Button>
          <LoadingButton variant="contained" color="error" onClick={handleReject} loading={isSubmitting}>
            Reject
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Reimbursement Request</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this reimbursement request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
          <LoadingButton variant="contained" color="error" onClick={handleCancel} loading={isSubmitting}>
            Yes, Cancel
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReimbursementDetails;
